import { HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { authUrl, refreshAuthUrl, UserStore, UserTokenStateService } from '@lib/user/data-access';
import { LOGOUT_ERROR_REDIRECTS } from '@shared/utils/interceptors';
import { BaseResponse } from '@shared/utils/interfaces';
import { map, Observable, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';

export const tokenInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const userStore = inject(UserStore);
  const userTokenStateService = inject(UserTokenStateService);
  const trustedUrls: string[] = [environment.apiUrl, environment.apiUrlV2];
  const blackListUrl: string[] = [refreshAuthUrl(), authUrl()];

  const shouldRefresh = userStore.isRefreshTokenAllowed;

  const addToken = (request: HttpRequest<unknown>): Observable<HttpEvent<unknown>> => {
    const token = userStore.token();
    if (blackListUrl.includes(request.url)) {
      return next(request);
    }

    const clonedReq = request.clone({
      withCredentials: false,
      ...(token && {
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      }),
    });

    return next(clonedReq);
  };

  const handleResponse = (event: HttpEvent<unknown>): HttpEvent<unknown> => {
    const isTrustedResponse = event instanceof HttpResponse && trustedUrls.some((url) => req.url.startsWith(url));

    if (isTrustedResponse) {
      const responseBody = event.body as BaseResponse<unknown>;

      if (responseBody.success === false) {
        const error = responseBody.errors?.[0];
        const redirect = error?.code ? LOGOUT_ERROR_REDIRECTS[error.code] : undefined;

        if (redirect) {
          userStore.logout(redirect);
        }
      }
    }

    return event;
  };

  if (!trustedUrls.some((url) => req.url.startsWith(url))) {
    return next(req);
  }

  if (req.url === refreshAuthUrl()) {
    return next(req).pipe(map((event) => handleResponse(event)));
  }

  if (shouldRefresh()) {
    if (userTokenStateService.getIsRefreshing()) {
      return userTokenStateService.waitUntilRefreshEnds().pipe(switchMap(() => addToken(req).pipe(map((event) => handleResponse(event)))));
    }

    return userStore.refreshAccessToken().pipe(switchMap(() => addToken(req).pipe(map((event) => handleResponse(event)))));
  } else {
    return addToken(req).pipe(map((event) => handleResponse(event)));
  }
};
